import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { listLineages } from 'features/evals3/api/evals3Api';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { KIND_LABEL, kindOf, filterSummary, statusOf, rowsText } from 'features/datasets/utils/sources';
import {
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

/**
 * Sources for one dataset: a table over a blurred page. Edit opens the source
 * editor (an agent opens the agent). Run now and Remove act on that source.
 */
export default function SourcesDialog({ sources, canWrite, onClose, onEdit, onRun, onRemove }) {
  const { team } = useParams();
  const navigate = useNavigate();
  const teamId = getSelectedTeamNumericId();
  const [lineages, setLineages] = useState([]);

  useEffect(() => {
    if (!teamId) return undefined;
    let off = false;
    Promise.resolve(listLineages(teamId)).then((l) => { if (!off) setLineages(l || []); }).catch(() => { if (!off) setLineages([]); });
    return () => { off = true; };
  }, [teamId]);
  const names = useMemo(() => Object.fromEntries((lineages || []).map((l) => [l.id, l.name])), [lineages]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = Array.isArray(sources) ? sources : [];
  const edit = (src) => {
    onClose();
    if (src.agent) navigate(`/${encodeURIComponent(team)}/agents/${src.agent.id}`);
    else onEdit?.(src);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[10vh] backdrop-blur-sm"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-label="Sources"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[80vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <h2 className="text-[14px] font-semibold text-[color:var(--text-primary)]">Sources</h2>
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          {list.length === 0 ? (
            <p className="py-8 text-center text-[12px] text-[color:var(--text-muted)]">No sources yet.</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-[color:var(--border-default)]">
              <table className="w-full text-left">
                <thead>
                  <tr className={tableHeaderRowClass}>
                    {['Source', 'Filter', 'Status', 'Rows', 'Actions'].map((label) => (
                      <th key={label} scope="col" className={tableHeaderCellClass}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((src) => {
                    const st = statusOf(src);
                    const kind = src.agent ? `Agent · ${src.agent.name || 'agent'}` : (KIND_LABEL[kindOf(src)] || kindOf(src));
                    return (
                      <tr key={src.id} className={tableRowClass}>
                        <td className={`${tableDataCellClass} font-medium`}>{kind}</td>
                        <td className={`${tableDataCellClass} max-w-[240px] text-[color:var(--text-muted)]`}><span className="block truncate">{filterSummary(src, names) || '—'}</span></td>
                        <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>{st.text || '—'}</td>
                        <td className={`${tableDataCellClass} tabular-nums text-[color:var(--text-muted)]`}>{src.agent ? '—' : rowsText(src)}</td>
                        <td className={tableDataCellClass}>
                          {canWrite ? (
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => edit(src)} className="text-[12px] text-[color:var(--text-primary)] hover:underline">Edit</button>
                              {!src.agent ? <button type="button" onClick={() => onRun?.(src)} className="text-[12px] text-[color:var(--text-primary)] hover:underline">Run now</button> : null}
                              {!src.agent ? <button type="button" onClick={() => onRemove?.(src)} className="text-[12px] text-danger hover:underline">Remove</button> : null}
                            </div>
                          ) : <span className="text-[color:var(--text-muted)]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
